/**
 * Zustand store global para el estado de llamadas Telnyx.
 *
 * Persiste entre navegación de rutas CRM — el sidebar permanece abierto
 * mientras el agente navega entre contactos, expedientes, etc.
 *
 * P4: Questionnaire se persiste en sessionStorage para sobrevivir recargas.
 */

import { create } from 'zustand';
import type {
  CallStore,
  CallContact,
  CallOrigin,
  QuestionnaireData,
  CallSidebarState,
} from '@/types/callSidebar';
import type { TranscriptionSegment, TranscriptionWSStatus } from '@/types/transcription';

const emptyQuestionnaire: QuestionnaireData = {
  call_type: '',
  city: '',
  state: '',
  nacionalidad: '',
  fecha_llegada_espana: '',
  empadronado: undefined,
  tiene_familiares_espana: undefined,
  familiares_espana_detalle: '',
  profesion: '',
  trabajando_actualmente: undefined,
  tipo_trabajo_detalle: '',
  estado_civil: '',
  hijos_menores: undefined,
  nivel_estudios: '',
  tiene_documentacion: undefined,
  documentos_disponibles: [],
  servicio_propuesto: '',
  servicio_detalle: '',
  urgencia_cliente: '',
  grading_llamada: '',
  grading_situacion: '',
  probabilidad_conversion: undefined,
  notes: '',
  call_result: '',
  follow_up_date: '',
  follow_up_responsible_id: '',
  proxima_accion_fecha: '',
};

// ── SessionStorage persistence (P4: survives page reload) ────────────────────

const SESSION_STORAGE_KEY = 'call_gathering_backup';

function restoreQuestionnaire(): QuestionnaireData {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (raw) return { ...emptyQuestionnaire, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...emptyQuestionnaire };
}

function persistQuestionnaire(q: QuestionnaireData): void {
  try { sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(q)); } catch { /* ignore */ }
}

function clearPersistedQuestionnaire(): void {
  try { sessionStorage.removeItem(SESSION_STORAGE_KEY); } catch { /* ignore */ }
}

// ─────────────────────────────────────────────────────────────────────────────

const initialState: CallSidebarState = {
  isOpen: false,
  phase: 'idle',
  contact: null,
  responsibleUserId: null,
  questionnaire: { ...emptyQuestionnaire },
  activeTab: 'questionnaire',
  seconds: 0,
  startedAt: null,
  activeCallId: null,
  origin: null,
  transcriptionSegments: [],
  transcriptionWsStatus: 'idle' as TranscriptionWSStatus,
  isCapturing: false,
  transcriptionError: null,
};

export const useCallStore = create<CallStore>((set) => ({
  ...initialState,

  startCall: (contact: CallContact, responsibleUserId?: string, origin?: CallOrigin) => {
    // Try restoring questionnaire from a previous interrupted session
    const restored = restoreQuestionnaire();
    const hasRestoredData = Object.values(restored).some(
      (v) => v !== '' && v !== undefined
    );
    set({
      isOpen: true,
      phase: 'connecting',
      contact,
      responsibleUserId: responsibleUserId ?? null,
      questionnaire: hasRestoredData ? restored : { ...emptyQuestionnaire },
      activeTab: 'questionnaire',
      seconds: 0,
      startedAt: new Date().toISOString(),
      activeCallId: null,
      origin: origin ?? 'contact',
      transcriptionSegments: [],
      transcriptionWsStatus: 'idle' as TranscriptionWSStatus,
      isCapturing: false,
      transcriptionError: null,
    });
  },

  setActive: () =>
    set({ phase: 'active' }),

  setPostCall: () =>
    set({ phase: 'post_call' }),

  close: () => {
    clearPersistedQuestionnaire();
    set({ ...initialState });
  },

  setActiveTab: (tab) =>
    set({ activeTab: tab }),

  updateQuestionnaire: (patch: Partial<QuestionnaireData>) =>
    set((s) => {
      const updated = { ...s.questionnaire, ...patch };
      persistQuestionnaire(updated);
      return { questionnaire: updated };
    }),

  setActiveCallId: (callId: string | null) =>
    set({ activeCallId: callId }),

  tick: () =>
    set((s) => ({ seconds: s.seconds + 1 })),

  updateTranscription: (patch: {
    segments?: TranscriptionSegment[];
    wsStatus?: TranscriptionWSStatus;
    isCapturing?: boolean;
    error?: string | null;
  }) =>
    set(() => ({
      ...(patch.segments !== undefined && { transcriptionSegments: patch.segments }),
      ...(patch.wsStatus !== undefined && { transcriptionWsStatus: patch.wsStatus }),
      ...(patch.isCapturing !== undefined && { isCapturing: patch.isCapturing }),
      ...(patch.error !== undefined && { transcriptionError: patch.error }),
    })),
}));
